<template>
  <div ref="containerRef">
    <div
        v-if="retryCounter > maxRetries && !imageUrl.includes('.mp4')"
        class="relative w-full aspect-square overflow-hidden"
    >
      <img
          v-if="typeof mainMetal != 'undefined' && mainMetal != ''"
          :alt="$t(pageStr + '.main_metal_background')"
          :src="
            'https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/backgrounds/' +
            mainMetal +
            '_' +
            image_pos +
            '.webp'
          "
          class="absolute inset-0 w-full h-full object-cover"
      />
      <img
          src="@/assets/img/ring-placeholder.svg"
          :alt="$t(pageStr + '.error_unable_to_load_image')"
          class="relative w-full h-full object-contain"
          width="600"
          height="600"
      />
    </div>
    <template v-else-if="finaleImageUrl != ''">
      <div class="relative flex items-center justify-center h-full w-full">
        <div v-if="finaleImageUrl.includes('.mp4')" class="w-full">
          <video class="mx-auto w-full relative z-30" autoplay loop muted playsinline>
            <source :src="finaleImageUrl" type="video/mp4">
            {{ $t(pageStr + '.video_not_supported') }}
          </video>
        </div>
        <div
            v-else
            class="relative w-full h-full overflow-hidden flex items-center justify-center"
            :class="{'cursor-zoom-in': isZoomAllowed}"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseLeave"
        >
          <img
              v-if="typeof mainMetal  != 'undefined' && mainMetal != '' && isOldImage === false"
              :alt="$t(pageStr + '.main_metal_background')"
              :src="
            'https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/backgrounds/' +
            mainMetal +
            '_' +
            image_pos +
            '.webp'
          "
              class="z-10 absolute inset-0"
              :class="getFocusModeCssClasses()"
              :style="zoomStyle"
          />
          <!-- shadow for wedding rings -->
          <img v-if="isWeddingRing" src="https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/shadow/universal_shadow.v2.webp"
              class="z-10 absolute inset-0"
              :class="getFocusModeCssClasses()"
              :style="zoomStyle"
          />
          <!-- Main image with layer image -->
          <img
              :src="finaleImageUrl"
              class="z-10 relative m-auto "
              :class="getFocusModeCssClasses()"
              :style="zoomStyle"
          />
          <img
              v-if="finaleImageLayerUrl != ''"
              :src="finaleImageLayerUrl"
              class="absolute inset-0 mt-0 z-30 m-auto"
              :class="getFocusModeCssClasses()"
              :style="zoomStyle"
          />
        </div>
      </div>
    </template>
    <div class="relative top-0 left-0" v-else>
      <div
          class="flex items-end justify-center h-64 pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
            class="inset-0 bg-gray-100 bg-opacity-75 transition-opacity"
            aria-hidden="true"
        ></div>

        <span class="hidden sm:inline-block sm:align-middle" aria-hidden="true"
        >&#8203;</span
        >
        <div
            class="inline-block align-middle overflow-hidden object-center transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
        >
          <div class="lds-ripple border-brand-main">
            <div class="border-brand-main"></div>
            <div class="border-brand-main"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";

const props = withDefaults(
    defineProps<{
      imageUrl?: string;
      imageLayerUrl?: string;
      mainMetal?: string;
      zoom?: boolean;
      focusMode?: boolean;
      focusModeWidth?: string | number;
      focusModeIdx?: string | number;
      isWeddingRing?: boolean;
    }>(),
    {
      imageUrl: "",
      imageLayerUrl: "",
      mainMetal: "",
      zoom: false,
      focusMode: false,
      focusModeWidth: "",
      focusModeIdx: "",
      isWeddingRing: false,
    }
);

const finaleImageUrl = ref("");
const finaleImageLayerUrl = ref("");
const isUnmounted = ref(false);
const isVisible = ref(false);
const containerRef = ref<HTMLElement | null>(null);
let intersectionObserver: IntersectionObserver | null = null;

onMounted(() => {
  intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isVisible.value) {
          isVisible.value = true;
          loadImage(props.imageUrl);
          loadLayerImage(props.imageLayerUrl);
          intersectionObserver?.disconnect();
        }
      },
      {threshold: 0.01}
  );
  if (containerRef.value) {
    intersectionObserver.observe(containerRef.value);
  }
});

onBeforeUnmount(() => {
  isUnmounted.value = true;
  intersectionObserver?.disconnect();
});

const isZooming = ref(false);
const zoomX = ref(0);
const zoomY = ref(0);

const handleMouseDown = (event: MouseEvent) => {
  if (isZoomAllowed.value && event.button === 0) {
    event.preventDefault();
    isZooming.value = true;
    updateZoomPosition(event);
  }
};

const handleMouseMove = (event: MouseEvent) => {
  if (isZooming.value) {
    updateZoomPosition(event);
  }
};

const handleMouseUp = () => {
  isZooming.value = false;
};

const handleMouseLeave = () => {
  isZooming.value = false;
};

const updateZoomPosition = (event: MouseEvent) => {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  zoomX.value = x;
  zoomY.value = y;
};

const zoomStyle = computed(() => {
  if (!isZooming.value) {
    return {};
  }
  return {
    transform: `scale(3.5)`,
    transformOrigin: `${zoomX.value}% ${zoomY.value}%`,
  };
});

const retryCounter = ref(0);
const retryCounterLayer = ref(0);
const maxRetries = 10;
const isOldImage = ref(false);
const pageStr = "components.itemImage"

const isZoomAllowed = computed(() => {
  return !(!props.zoom || props.focusMode ||
      props.imageUrl == "" ||
      props.imageUrl.includes('closeup-') ||
      props.imageUrl.includes('model-') ||
      props.imageUrl.includes('still-'));
})

/**
 * Image position
 */

const image_pos = computed(() => {
  if (props.imageUrl.includes("/pos")) {
    const imageUrlParts = props.imageUrl.split("/");

    return imageUrlParts[imageUrlParts.length - 2];
  }

  return "pos1";
});

/**
 * Das Bild wird so lange neu geladen, bis es vom Server komplett generiert wurde.
 * @param imageUrl
 */
const loadImage = (imageUrl: string) => {
  if (imageUrl.includes("mp4")) {
    finaleImageUrl.value = imageUrl;
    retryCounter.value = 0;
  } else {
    // console.debug("Try to load", imageUrl);
    retryCounter.value += 1;
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      if (isUnmounted.value) return;
      finaleImageUrl.value = imageUrl;
      retryCounter.value = 0;
      isOldImage.value = img.naturalWidth == 600;
    };
    img.onerror = () => {
      if (isUnmounted.value) return;
      if (retryCounter.value <= maxRetries) {
        setTimeout(loadImage, 100, imageUrl);
      }
    };
  }
};

const loadLayerImage = (imageLayerUrl: string) => {
  if (imageLayerUrl != "" && typeof imageLayerUrl != "undefined") {
    // console.debug("Try to load", imageLayerUrl);
    retryCounterLayer.value += 1;
    const img = new Image();
    img.src = imageLayerUrl;

    img.onload = () => {
      if (isUnmounted.value) return;
      finaleImageLayerUrl.value = imageLayerUrl;
    };
    img.onerror = () => {
      if (isUnmounted.value) return;
      if (retryCounterLayer.value <= maxRetries) {
        setTimeout(loadImage, 600, imageLayerUrl);
      }
    };
  } else {
    finaleImageLayerUrl.value = "";
  }
};

/**
 * Watcher überwacht, ob sich das Bild verändert hat, und startet den Lade-Prozess erneut
 */
watch(
    () => props.imageUrl,
    (imageUrl: string) => {
      if (isVisible.value) {
        loadImage(imageUrl);
      }
    },
    {deep: true},
);

watch(
    () => props.imageLayerUrl,
    (imageUrl: string) => {
      if (isVisible.value) {
        loadLayerImage(imageUrl);
      }
    },
    {deep: true},
);

/**
 * Focus mode
 */

const focusModeParameter: Record<string, Record<string, string>> = {
  // ok
  "default": {
    "2.0": "max-w-none w-72 h-72 object-cover -top-38 left-18",
    "2.2": "max-w-none w-71 h-71 object-cover -top-38 left-18",
    "2.5": "max-w-none w-70 h-70 object-cover -top-36 left-17",
    "2.7": "max-w-none w-68 h-68 object-cover -top-34 left-16",
    "3.0": "max-w-none w-66 h-66 object-cover -top-32 left-16",
    "3.5": "max-w-none w-64 h-64 object-cover -top-30 left-15",
    "4.0": "max-w-none w-61 h-61 object-cover -top-29 left-14",
    "4.5": "max-w-none w-59 h-59 object-cover -top-27 left-14",
    "5.0": "max-w-none w-57 h-57 object-cover -top-26 left-13",
    "5.5": "max-w-none w-54 h-54 object-cover -top-24 left-12",
    "6.0": "max-w-none w-52 h-52 object-cover -top-22 left-12",
    "6.5": "max-w-none w-49 h-49 object-cover -top-20 left-11",
    "7.0": "max-w-none w-46 h-46 object-cover -top-18 left-11",
    "7.5": "max-w-none w-43 h-43 object-cover -top-16 left-10",
    "8.0": "max-w-none w-40 h-40 object-cover -top-15 left-8",
    "8.5": "max-w-none w-37 h-37 object-cover -top-13 left-7",
    "9.0": "max-w-none w-34 h-34 object-cover -top-11 left-6",
    "9.5": "max-w-none w-31 h-31 object-cover -top-9 left-5",
    "10.0": "max-w-none w-28 h-28 object-cover -top-7 left-4",
    "11.0": "max-w-none w-24 h-24 object-cover -top-5 left-3",
    "12.0": "max-w-none w-20 h-20 object-cover -top-3 left-2",
  },
  "profile": {
    "2.0": "w-36 h-36 object-cover -top-12 left-0",
    "2.2": "w-36 h-36 object-cover -top-12 left-0",
    "2.5": "w-36 h-36 object-cover -top-12 left-0",
    "2.7": "w-36 h-36 object-cover -top-12 left-0",
    "3.0": "w-36 h-36 object-cover -top-12 left-0",
    "3.5": "w-36 h-36 object-cover -top-12 left-0",
    "4.0": "w-36 h-36 object-cover -top-12 left-0",
    "4.5": "w-36 h-36 object-cover -top-12 left-0",
    "5.0": "w-36 h-36 object-cover -top-12 left-0",
    "5.5": "w-36 h-36 object-cover -top-12 left-0",
    "6.0": "w-46 h-46 object-cover -top-14 left-0",
    "6.5": "w-36 h-36 object-cover -top-12 left-0",
    "7.0": "w-36 h-36 object-cover -top-12 left-0",
    "7.5": "w-36 h-36 object-cover -top-12 left-0",
    "8.0": "w-36 h-36 object-cover -top-12 left-0",
    "8.5": "w-36 h-36 object-cover -top-12 left-0",
    "9.0": "w-36 h-36 object-cover -top-12 left-0",
    "9.5": "w-36 h-36 object-cover -top-12 left-0",
    "10.0": "w-36 h-36 object-cover -top-12 left-0",
    "11.0": "w-36 h-36 object-cover -top-12 left-0",
    "12.0": "w-36 h-36 object-cover -top-12 left-0",
  },
  // ok
  "width": {
    "2.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "2.2": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "2.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "2.7": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "3.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "3.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "4.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "4.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "5.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "5.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "6.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "6.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "7.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "7.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "8.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "8.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "9.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "9.5": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "10.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "11.0": "max-w-none w-32 h-32 object-cover -top-10 left-6",
    "12.0": "max-w-none w-40 h-40 object-cover -top-24 left-12",
  },
  "grooves.*.groove_type": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
  // ok
  "grooves.*.width": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
  "stones.stone_presets": {
    "2.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.2": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.7": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "10.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "11.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "12.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
  },
  "stones.*.stone_size": {
    "2.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.2": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "2.7": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "3.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "4.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "5.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "6.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "7.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "8.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "9.5": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "10.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "11.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
    "12.0": "max-w-none w-40 h-40 object-cover -top-16 left-0",
  },
  "discs.*.color": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
  "discs.*.finish": {
    "2.0": "max-w-none w-50 h-50 object-cover -top-26 left-12",
    "2.2": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.5": "max-w-none w-49 h-49 object-cover -top-25 left-12",
    "2.7": "max-w-none w-48 h-48 object-cover -top-24 left-12",
    "3.0": "max-w-none w-47 h-47 object-cover -top-23 left-12",
    "3.5": "max-w-none w-46 h-46 object-cover -top-22 left-11",
    "4.0": "max-w-none w-44 h-44 object-cover -top-21 left-11",
    "4.5": "max-w-none w-43 h-43 object-cover -top-20 left-11",
    "5.0": "max-w-none w-42 h-42 object-cover -top-19 left-10",
    "5.5": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.0": "max-w-none w-40 h-40 object-cover -top-18 left-10",
    "6.5": "max-w-none w-39 h-39 object-cover -top-17 left-9",
    "7.0": "max-w-none w-38 h-38 object-cover -top-16 left-9",
    "7.5": "max-w-none w-36 h-36 object-cover -top-15 left-8",
    "8.0": "max-w-none w-34 h-34 object-cover -top-14 left-8",
    "8.5": "max-w-none w-33 h-33 object-cover -top-13 left-7",
    "9.0": "max-w-none w-32 h-32 object-cover -top-12 left-7",
    "9.5": "max-w-none w-30 h-30 object-cover -top-11 left-6",
    "10.0": "max-w-none w-29 h-29 object-cover -top-10 left-6",
    "11.0": "max-w-none w-28 h-28 object-cover -top-9 left-5",
    "12.0": "max-w-none w-28 h-28 object-cover -top-8 left-5",
  },
}

const getFocusModeCssClasses = () => {
  if (props.focusMode) {
    const widthStr = Number(props.focusModeWidth).toFixed(1).toString()
    let idxKey = props.focusModeIdx ? props.focusModeIdx.toString().trim() : ""



    // spilt idxKey by ".", if the second part is a number, replace it with the char "*" and join the idxKey again by "."
    const idxKeySplitted = idxKey.split(".")
    if (idxKeySplitted.length > 1 && !isNaN(idxKeySplitted[1])) {
      idxKeySplitted[1] = "*"
      idxKey = idxKeySplitted.join(".")
    }

    console.log("-" + idxKey + "-", widthStr, typeof focusModeParameter[idxKey], typeof focusModeParameter[idxKey][widthStr])

    if (
        idxKey !== "" &&
        typeof focusModeParameter[idxKey] !== "undefined" &&
        typeof focusModeParameter[idxKey][widthStr] === "string"
    ) {
      return focusModeParameter[idxKey][widthStr]
    } else if (typeof focusModeParameter.default[widthStr] === "string") {
      return focusModeParameter.default[widthStr]
    }

    return focusModeParameter.default["5.0"]
  } else {
    return "w-full"
  }
}

</script>

<style scoped>
.lds-ripple {
  display: inline-block;
  position: relative;
  width: 10em;
  height: 10em;
}

.lds-ripple div {
  position: absolute;
  border: 4px solid #a99d8b;
  opacity: 1;
  border-radius: 50%;
  animation: lds-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
}

.lds-ripple div:nth-child(2) {
  animation-delay: -0.5s;
}

@keyframes lds-ripple {
  0% {
    top: 4em;
    left: 4em;
    width: 0;
    height: 0;
    opacity: 1;
  }
  100% {
    top: 0;
    left: 0;
    width: 8em;
    height: 8em;
    opacity: 0;
  }
}
</style>
